
import { useState, useEffect } from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { fetchCampaignQuestions } from "@/services/modalCampaigns";
import { toast } from "sonner";

// Import from our new modular files
import { campaignFormSchema, CampaignFormValues, UseCampaignFormProps } from "./types/formTypes";
import { useQuestionManager } from "./hooks/useQuestionManager";
import { validateFormSubmission } from "./utils/formValidation";
import { handleFormSubmission } from "./utils/formSubmission";

export const useCampaignForm = ({ 
  currentUser, 
  existingCampaign, 
  assignedUsers, 
  onSuccess 
}: UseCampaignFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Use our extracted question manager hook
  const { 
    questions, 
    setQuestions, 
    addQuestion, 
    updateQuestion, 
    removeQuestion, 
    handleQuestionsGenerated 
  } = useQuestionManager();
  
  // Create form with default values
  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: {
      title: "",
      content: "",
      start_date: new Date(),
      end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // Default 1 week from now
      frequency: "once",
      users: [],
      has_quiz: false,
      min_score: 1,
      max_attempts: undefined,
      repeat_on_failure: true,
      allow_dismiss: true,
    },
  });
  
  // Load existing campaign data if editing
  useEffect(() => {
    const loadExistingData = async () => {
      if (!existingCampaign) return;
      
      // Set form values
      form.reset({
        title: existingCampaign.title,
        content: existingCampaign.content,
        start_date: new Date(existingCampaign.start_date),
        end_date: new Date(existingCampaign.end_date),
        frequency: existingCampaign.frequency,
        users: assignedUsers,
        has_quiz: existingCampaign.has_quiz,
        min_score: existingCampaign.min_score || 1,
        max_attempts: existingCampaign.max_attempts || undefined,
        repeat_on_failure: existingCampaign.repeat_on_failure,
        allow_dismiss: existingCampaign.allow_dismiss,
      });
      
      // Load questions if this is a quiz
      if (existingCampaign.has_quiz) {
        try {
          const campaignQuestions = await fetchCampaignQuestions(existingCampaign.id);
          setQuestions(campaignQuestions.map(q => ({
            id: q.id,
            question_text: q.question_text,
            options: q.options,
            correct_options: q.correct_options,
            points: q.points,
            order_number: q.order_number
          })));
        } catch (error) {
          console.error('Error loading questions:', error);
          toast.error('Fout bij het laden van de vragen');
        }
      }
    };
    
    loadExistingData();
  }, [existingCampaign, assignedUsers, form, setQuestions]);
  
  // Handle quiz toggle
  const handleQuizToggle = (enabled: boolean) => {
    form.setValue("has_quiz", enabled);
    
    // Add default question if enabling quiz for the first time
    if (enabled && questions.length === 0) {
      addQuestion();
    }
  };
  
  // Handle form submission
  const onSubmit = async (values: CampaignFormValues) => {
    // Validate the form submission
    if (!validateFormSubmission(values, questions)) {
      return;
    }

    // Handle the actual submission
    await handleFormSubmission(
      values,
      questions,
      currentUser,
      existingCampaign,
      onSuccess,
      setIsSubmitting
    );
  };
  
  return {
    form,
    questions,
    isSubmitting,
    handleQuizToggle,
    addQuestion,
    updateQuestion,
    removeQuestion,
    handleQuestionsGenerated,
    onSubmit
  };
};

// Re-export the types for convenience
export type { CampaignFormValues };
